import React, { useCallback, useEffect, useState } from 'react';
import AuthLayout from '../AuthLayout';
import { useImmer } from 'use-immer';
import { ILoginInput, LoginStep } from './types';
import {
  ChangePasswordForm,
  LoginForm,
  MfaSetupForm,
  MfaVerifyForm,
} from './forms';
import ForgotPasswordForm from './forms/ForgotPasswordForm';
import { useBroadcastChannel } from '../../../utils/hooks';
import { useNavigate } from 'react-router-dom';
import { useAnalytics } from '../../../utils/analytics/useAnalytics';
import { pageViewed } from '../../../utils/analytics/events/page/events';
import { UnreachableCaseError } from 'ts-essentials';

const analyticsValueByStep = {
  [LoginStep.NotLoggedIn]: 'not logged in',
  [LoginStep.ChangePassword]: 'change password',
  [LoginStep.MfaSetup]: 'mfa setup',
  [LoginStep.MfaVerify]: 'mfa verify',
  [LoginStep.ForgotPassword]: 'forgot password',
  [LoginStep.ConfirmForgotPassword]: 'confirm forgot password',
};

const Login = () => {
  const navigate = useNavigate();
  const [currLoginStep, setCurrLoginStep] = useState<LoginStep>(
    LoginStep.NotLoggedIn,
  );
  const [loginInput, setLoginInput] = useImmer<ILoginInput>({
    email: '',
    password: '',
  });

  // message handler must be stable reference, otherwise it causes effect
  // in useBroadcastChannel on every rerender
  const loginMessageHandler = useCallback(() => {
    navigate('/');
  }, [navigate]);

  useBroadcastChannel({
    channelName: 'login',
    messageHandler: loginMessageHandler,
  });

  const { track } = useAnalytics();
  useEffect(() => {
    track(
      pageViewed({
        category: 'login',
        name: 'login',
        id: analyticsValueByStep[currLoginStep],
      }),
    );
  }, [currLoginStep, track]);

  const getFormByLoginStep = () => {
    switch (currLoginStep) {
      case LoginStep.NotLoggedIn:
        return (
          <LoginForm
            setLoginInput={setLoginInput}
            loginInput={loginInput}
            setLoginStep={setCurrLoginStep}
          />
        );
      case LoginStep.MfaSetup:
        return (
          <MfaSetupForm
            email={loginInput.email}
            setLoginStep={setCurrLoginStep}
            setLoginInput={setLoginInput}
          />
        );
      case LoginStep.MfaVerify:
        return (
          <MfaVerifyForm
            email={loginInput.email}
            setLoginStep={setCurrLoginStep}
            setLoginInput={setLoginInput}
          />
        );
      case LoginStep.ForgotPassword:
        return (
          <ForgotPasswordForm
            setLoginStep={setCurrLoginStep}
            setLoginInput={setLoginInput}
            loginInput={loginInput}
          />
        );
      case LoginStep.ConfirmForgotPassword:
      case LoginStep.ChangePassword:
        return (
          <ChangePasswordForm
            email={loginInput.email}
            setLoginStep={setCurrLoginStep}
            loginStep={currLoginStep}
            setLoginInput={setLoginInput}
          />
        );
      default:
        throw new UnreachableCaseError(currLoginStep);
    }
  };

  const getLoginTitleByStep = () => {
    switch (currLoginStep) {
      case LoginStep.NotLoggedIn:
        return 'Login to your account';
      case LoginStep.MfaSetup:
        return 'Add authenticator app';
      case LoginStep.MfaVerify:
        return 'Enter the one-time code';
      case LoginStep.ForgotPassword:
        return 'Forgot Password';
      case LoginStep.ChangePassword:
      case LoginStep.ConfirmForgotPassword:
        return 'Change Password';
      default:
        throw new UnreachableCaseError(currLoginStep);
    }
  };

  return (
    <AuthLayout heading={getLoginTitleByStep()}>
      {getFormByLoginStep()}
    </AuthLayout>
  );
};

export default Login;
